import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Award, Users, Briefcase, Sparkles, Building2 } from 'lucide-react'

const leadership = [
  {
    name: 'Mr. Faheem Shaikh',
    role: 'Co-Founder & Managing Director',
    description: 'Software Engineer with 14+ years of experience. Founded Eonixa Dynamics in 2009. Scaled the enterprise to ₹150 Crores valuation. Expert in Government Projects & Tender Ecosystem.',
    icon: Building2,
    color: '#6366f1',
  },
  {
    name: 'Mr. Vivek Mumbre',
    role: 'Partner / Director',
    description: 'Strategic investment guidance and financial oversight. Expert in business growth strategies and market expansion.',
    icon: Briefcase,
    color: '#8b5cf6',
  },
  {
    name: 'Mr. Ritesh Jaiswal',
    role: 'Partner / Director',
    description: 'Leading operations and project execution. Driving excellence in government project delivery and client relationships.',
    icon: Users,
    color: '#ec4899',
  },
  {
    name: 'Mr. Jamir Shaikh',
    role: 'Executive Director',
    description: 'Strategic planning and business development. Expertise in institutional projects and government partnerships.',
    icon: Award,
    color: '#06b6d4',
  },
  {
    name: 'Mr. Zakir Sayyed',
    role: 'Executive Director',
    description: 'Technology initiatives and innovation. Driving digital transformation and technology solutions across projects.',
    icon: Sparkles,
    color: '#10b981',
  },
]

const stats = [
  { icon: Users, value: '50+', label: 'Team Members' },
  { icon: Award, value: '15+', label: 'Years Experience' },
  { icon: Building2, value: '50+', label: 'Government Clients' },
]

const Team = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  })

  return (
    <>
      <Helmet>
        <title>Our Team | Eonixa Dynamics - Leadership</title>
        <meta name="description" content="Meet our leadership team - experienced professionals driving innovation in Government Technology and Enterprise Solutions" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[50vh] overflow-hidden pt-24">
        <div className="container-enterprise relative z-10 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-6"
              style={{
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
                border: '1px solid rgba(99, 102, 241, 0.25)',
              }}
            >
              <Users className="w-4 h-4 text-primary" />
              <span className="text-sm text-slate-300">Our Leadership</span>
            </motion.div>
            <h1 className="mb-6 text-4xl font-bold text-white sm:text-5xl">
              Leadership <span className="gradient-text">Team</span>
            </h1>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto leading-relaxed text-justify">
              Our organization is driven by a team of experienced professionals committed to excellence, innovation, and sustainable growth in government and institutional projects.
            </p>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex flex-wrap justify-center gap-8 mt-12"
          >
            {stats.map((stat) => {
              const Icon = stat.icon
              return (
                <div key={stat.label} className="text-center">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-3"
                    style={{
                      background: 'rgba(99, 102, 241, 0.1)',
                      border: '1px solid rgba(99, 102, 241, 0.2)',
                    }}
                  >
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <p className="text-3xl font-bold gradient-text">{stat.value}</p>
                  <p className="text-sm text-slate-400">{stat.label}</p>
                </div>
              )
            })}
          </motion.div>
        </div>
      </section>

      {/* Leadership Grid */}
      <section ref={ref} className="py-24 relative overflow-hidden">
        <div className="container-enterprise relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Meet Our Leaders</h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Visionary leaders guiding Eonixa Dynamics towards excellence
            </p>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {leadership.map((leader, index) => {
              const Icon = leader.icon
              return (
                <motion.div
                  key={leader.name}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className="group relative"
                >
                  <div className="rounded-2xl p-8 h-full text-center"
                    style={{
                      background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                      backdropFilter: 'blur(12px)',
                    }}
                  >
                    {/* Icon */}
                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
                      style={{
                        background: `${leader.color}20`,
                        border: `1px solid ${leader.color}40`,
                      }}
                    >
                      <Icon className="w-8 h-8" style={{ color: leader.color }} />
                    </div>

                    {/* Name */}
                    <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-primary transition-colors">
                      {leader.name}
                    </h3>

                    {/* Role */}
                    <p className="text-sm font-medium mb-4"
                      style={{ color: leader.color }}
                    >
                      {leader.role}
                    </p>

                    {/* Description */}
                    <p className="text-sm text-slate-400 leading-relaxed text-justify">
                      {leader.description}
                    </p>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Join Team CTA */}
      <section className="py-16 relative overflow-hidden">
        <div className="container-enterprise relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <div className="max-w-2xl mx-auto rounded-3xl p-8"
              style={{
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.08) 100%)',
                border: '1px solid rgba(99, 102, 241, 0.15)',
              }}
            >
              <h2 className="text-2xl font-bold text-white mb-4">Join Our Growing Team</h2>
              <p className="text-slate-400 mb-6">
                We're always looking for talented individuals who are passionate about making a difference through technology.
              </p>
              <a
                href="/contact"
                className="btn-glow inline-flex items-center gap-2 px-8 py-4 text-white font-semibold"
              >
                <Sparkles className="w-5 h-5" />
                <span>View Open Positions</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}

export default Team